import { QueryClient } from 'react-query';

/**
 * Utility functions for invalidating calendar-related queries
 * when schedule data changes occur
 */

/**
 * Invalidates all calendar and schedule-related queries
 * Use this when any operation affects the schedule data
 */
export const invalidateCalendarQueries = (queryClient: QueryClient) => {
  console.log('🔄 invalidateCalendarQueries: Starting invalidation...');
  
  // Invalidate schedule queries - use predicate to match ['schedule', filters] pattern
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      const matches = Array.isArray(key) && key[0] === 'schedule';
      if (matches) {
        console.log('🎯 invalidateCalendarQueries: Invalidating schedule query:', key);
      }
      return matches;
    }
  });
  
  console.log('✅ invalidateCalendarQueries: Schedule queries invalidated');
  
  // Invalidate calendar-specific queries
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && 
             (key[0] === 'schedule-calendar' || 
              key[0] === 'conflicts-calendar' ||
              key[0] === 'dashboard-schedule');
    }
  });
  
  // Invalidate player queries (affects calendar display)
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'players' || (Array.isArray(key) && key[0] === 'players');
    }
  });
  
  // Invalidate team queries (affects match assignments)
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'teams' || (Array.isArray(key) && key[0] === 'teams');
    }
  });
  
  // Invalidate conflict queries
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'conflicts' || (Array.isArray(key) && key[0] === 'conflicts');
    }
  });
};

/**
 * Invalidates queries when player data changes
 * (adding, removing, or modifying players)
 */
export const invalidatePlayerQueries = (queryClient: QueryClient) => {
  // Player changes can affect schedule display
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'players' || (Array.isArray(key) && key[0] === 'players');
    }
  });
  
  // Invalidate calendar queries since player changes affect match display
  invalidateCalendarQueries(queryClient);
};

/**
 * Invalidates queries when team assignments change
 * (assigning/removing players from teams)
 */
export const invalidateTeamQueries = (queryClient: QueryClient) => {
  console.log('🔄 invalidateTeamQueries: Starting team query invalidation...');
  
  // Team changes affect schedule and conflicts
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'teams' || (Array.isArray(key) && key[0] === 'teams');
    }
  });
  
  console.log('🔄 invalidateTeamQueries: Calling invalidateCalendarQueries...');
  
  // Invalidate calendar queries since team changes affect match assignments
  invalidateCalendarQueries(queryClient);
  
  console.log('✅ invalidateTeamQueries: Team invalidation completed');
};

/**
 * Invalidates queries when league data changes
 * (importing, syncing, or modifying leagues)
 */
export const invalidateLeagueQueries = (queryClient: QueryClient) => {
  // League changes can bring in new schedule data
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'leagues' || (Array.isArray(key) && key[0] === 'leagues');
    }
  });
  
  // Invalidate all schedule-related data
  invalidateCalendarQueries(queryClient);
};

/**
 * Invalidates queries when sync operations complete
 * (league sync, schedule import, etc.)
 */
export const invalidateSyncQueries = (queryClient: QueryClient) => {
  // Sync operations can change all data
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'sync' || (Array.isArray(key) && key[0] === 'sync');
    }
  });
  
  // Invalidate all schedule and calendar data
  invalidateCalendarQueries(queryClient);
  
  // Also invalidate league data since sync affects leagues
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key === 'leagues' || (Array.isArray(key) && key[0] === 'leagues');
    }
  });
};

/**
 * Invalidates queries when match data changes
 * (creating, updating, or deleting matches)
 */
export const invalidateMatchQueries = (queryClient: QueryClient) => {
  // This is the most common case - direct schedule changes
  invalidateCalendarQueries(queryClient);
  
  // Also invalidate standings since match results affect standings
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === 'contextual-standings';
    }
  });
  
  console.log('✅ invalidateMatchQueries: Standings queries also invalidated');
};
